import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axios";

/* ── Avatar colour palette ── */
const AVATAR_PALETTE = [
  { bg: "#dde8fb", fg: "#1D5DA8" },
  { bg: "#fde8d8", fg: "#c05621" },
  { bg: "#d8f5e8", fg: "#276749" },
  { bg: "#f0ddfb", fg: "#6b21a8" },
  { bg: "#fdf5d8", fg: "#92400e" },
  { bg: "#ddf5fb", fg: "#0e7490" },
];

function getInitials(name = "") {
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
}

export default function AdminPanelForUsers() {
  const [data, setData] = useState({ users: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [hoveredRow, setHoveredRow] = useState(null);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 10;

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/admin/users-overview");
        setData(res.data);
      } catch {
        console.error("Access Denied or Server Error");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentUsers = data.users.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(data.users.length / entriesPerPage);

  const pageNums = [];
  const winStart = Math.max(1, Math.min(currentPage - 1, totalPages - 2));
  const winEnd = Math.min(totalPages, winStart + 2);
  for (let i = winStart; i <= winEnd; i++) pageNums.push(i);

  if (loading)
    return (
      <div
        style={{
          padding: 48,
          textAlign: "center",
          fontWeight: 600,
          color: "#1D5DA8",
          fontFamily: "inherit",
        }}
      >
        Initializing User Shield…
      </div>
    );

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />
      <style>{`
        .apu-root * { box-sizing: border-box; font-family: 'Inter', sans-serif; }
        .apu-row:hover { background: #f7f9ff !important; }
        .apu-arrow:hover:not(:disabled) { background: #f0f3fa !important; }
        .apu-pg:hover { background: #f0f3fa !important; }
      `}</style>

      <div
        className="apu-root"
        style={{
          padding: "24px 40px",
          background: "#f8fafc",
          height: "100vh",        // Changed from minHeight
          overflowY: "auto",      // ADDED THIS LINE
        }}
      >
        {/* ── Header ── */}
        <div style={{ marginBottom: 20 }}>
          <p
            style={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "1.3px",
              textTransform: "uppercase",
              color: "#1D5DA8",
              margin: "0 0 8px",
            }}
          >
            Kinexar Internal
          </p>

          <h2
            style={{
              fontSize: 26,
              fontWeight: 700,
              color: "#0f1b35",
              margin: "0 0 4px",
              letterSpacing: "-0.4px",
              lineHeight: 1.2,
            }}
          >
            User Infrastructure
          </h2>

          <p style={{ fontSize: 13, color: "#6b7fa3", margin: 0 }}>
            Classification based on Account Origin and Role Authority
          </p>
        </div>

        {/* ── Stats Panel ── */}
        <div style={{ display: "flex", gap: 24, marginBottom: 20, maxWidth: 760 }}>
          {/* Admins — dark */}
          <div
            style={{
              flex: "0 0 280px",
              background: "#0d2b8f",
              borderRadius: "16px",
              padding: "22px 26px 24px",
              boxShadow: "0 6px 18px rgba(13, 43, 143, 0.10)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="rgba(255,255,255,0.55)"
                strokeWidth="2.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "1.2px",
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.5)",
                }}
              >
                Direct Signups
              </span>
            </div>

            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "rgba(255,255,255,0.85)",
                letterSpacing: "0.6px",
                marginBottom: 8,
              }}
            >
              ADMINS
            </div>

            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <span style={{ fontSize: 38, fontWeight: 700, lineHeight: 1, color: "#fff" }}>
                {data.stats?.total_admins}
              </span>
              <span style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", fontWeight: 400 }}>
                Verified Entities
              </span>
            </div>
          </div>

          {/* Staff — white */}
          <div
            style={{
              flex: "0 0 280px",
              background: "#fff",
              borderRadius: "16px",
              padding: "22px 26px 24px",
              border: "1px solid #e2e8f4",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#8a97b0"
                strokeWidth="2.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
              </svg>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "1.2px",
                  textTransform: "uppercase",
                  color: "#8a97b0",
                }}
              >
                Invited Users
              </span>
            </div>

            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#1D5DA8",
                letterSpacing: "0.6px",
                marginBottom: 8,
              }}
            >
              STAFF
            </div>

            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <span style={{ fontSize: 38, fontWeight: 700, lineHeight: 1, color: "#1D5DA8" }}>
                {data.stats?.total_employees}
              </span>
              <span style={{ fontSize: 12, color: "#8a97b0", fontWeight: 400 }}>
                Active Invitations
              </span>
            </div>
          </div>
        </div>

        {/* ── Table Card ── */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            border: "1px solid #e2e8f4",
            overflow: "hidden",
            boxShadow: "0 1px 8px rgba(0,0,0,0.04)",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {[
                  { label: "User Entity", pl: 36 },
                  { label: "Method", pl: 20 },
                  { label: "Role Authority", pl: 20 },
                  { label: "Joined Date", pl: 20 },
                ].map(({ label, pl }) => (
                  <th
                    key={label}
                    style={{
                      padding: `12px 20px 12px ${pl}px`,
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "1.1px",
                      textTransform: "uppercase",
                      color: "#8a97b0",
                      textAlign: "left",
                      borderBottom: "1px solid #eef1f8",
                      background: "#fff",
                    }}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {currentUsers.map((user, idx) => {
                const globalIdx = indexOfFirst + idx;
                const pal = AVATAR_PALETTE[globalIdx % AVATAR_PALETTE.length];
                const isAdmin = user.is_admin;
                const isLast = idx === currentUsers.length - 1;

                return (
                  <tr
                    key={user.id}
                    className="apu-row"
                    onClick={() => navigate(`/sa-user-x9k2/${user.id}`)}
                    onMouseEnter={() => setHoveredRow(user.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    style={{
                      cursor: "pointer",
                      background: "transparent",
                      borderBottom: isLast ? "none" : "1px solid #f2f5fc",
                      transition: "background 0.12s",
                    }}
                  >
                    {/* User Entity */}
                    <td style={{ padding: "10px 20px 10px 36px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            background: pal.bg,
                            color: pal.fg,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 700,
                            fontSize: 12,
                            flexShrink: 0,
                            letterSpacing: "0.2px",
                          }}
                        >
                          {getInitials(user.name)}
                        </div>

                        <div>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: "#0f1b35",
                              lineHeight: 1.25,
                            }}
                          >
                            {user.name}
                          </div>

                          <div
                            style={{
                              fontSize: 11,
                              color: "#8a97b0",
                              marginTop: 1,
                              lineHeight: 1.2,
                            }}
                          >
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Method */}
                    <td style={{ padding: "10px 20px" }}>
                      <img
                        src={isAdmin ? "/images/internaldirect.svg" : "/images/internalemail.svg"}
                        alt={isAdmin ? "Direct Signup" : "Email Invitation"}
                        style={{
                          height: 24,
                          width: "auto",
                          display: "block",
                        }}
                      />
                    </td>

                    {/* Role Authority */}
                    <td style={{ padding: "10px 20px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 7,
                          fontSize: 13,
                          fontWeight: 500,
                          color: "#1a2740",
                        }}
                      >
                        {isAdmin && (
                          <img
                            src="/images/internalstar.svg"
                            alt="Admin"
                            style={{
                              width: 14,
                              height: 14,
                              objectFit: "contain",
                            }}
                          />
                        )}

                        {isAdmin ? "Admin" : user.assigned_role || "Staff"}
                      </div>
                    </td>

                    {/* Joined Date */}
                    <td
                      style={{
                        padding: "10px 20px",
                        fontSize: 13,
                        color: "#6b7fa3",
                        fontWeight: 400,
                      }}
                    >
                      {formatDate(user.created_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* ── Footer / Pagination ── */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 24px 12px 36px",
              borderTop: "1px solid #eef1f8",
            }}
          >
            <span style={{ fontSize: 12, color: "#8a97b0" }}>
              Showing {indexOfFirst + 1} of {data.users.length} established accounts
            </span>

            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <button
                className="apu-arrow"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  border: "1px solid #e2e8f4",
                  background: "transparent",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  opacity: currentPage === 1 ? 0.35 : 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 17,
                  color: "#4a5a7a",
                  transition: "background 0.12s",
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                ‹
              </button>

              {pageNums.map((n) => (
                <button
                  key={n}
                  className={n === currentPage ? "" : "apu-pg"}
                  onClick={() => setCurrentPage(n)}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    border: n === currentPage ? "none" : "1px solid #e2e8f4",
                    background: n === currentPage ? "#1D5DA8" : "transparent",
                    color: n === currentPage ? "#fff" : "#4a5a7a",
                    fontWeight: n === currentPage ? 700 : 500,
                    fontSize: 13,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "background 0.12s",
                    padding: 0,
                  }}
                >
                  {n}
                </button>
              ))}

              <button
                className="apu-arrow"
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage((p) => p + 1)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  border: "1px solid #e2e8f4",
                  background: "transparent",
                  cursor:
                    currentPage === totalPages || totalPages === 0
                      ? "not-allowed"
                      : "pointer",
                  opacity: currentPage === totalPages || totalPages === 0 ? 0.35 : 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 17,
                  color: "#4a5a7a",
                  transition: "background 0.12s",
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}